/**
 * English: System status dashboard — multi-service health, memory stats, quick links.
 * Thai: แดชบอร์ดสถานะระบบ — สุขภาพ services หลายตัว สถิติหน่วยความจำ ลิงก์ด่วน
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowPathIcon, ChatBubbleLeftRightIcon, LinkIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { API_BASE, ENDPOINTS } from '../config/api';
import PageShell from '../components/layout/PageShell';
import PageHeader from '../components/layout/PageHeader';
import { parseJsonResponse } from '../utils/httpJson';
import { useAuth } from '../context/AuthContext';

type ServiceStatus = {
  ok: boolean;
  latency_ms: number;
  checks: Record<string, { ok: boolean; detail: string }>;
};

type SystemStatusResponse = {
  checked_at: string;
  services: Record<string, ServiceStatus>;
};

type DailySummaryItem = {
  id: string;
  org_id: string;
  station_id: string | null;
  summary_date: string;
  summary_md: string;
  source_turn_count: number;
  created_at: string;
  updated_at: string;
};

function CheckRow({ name, ok, detail }: { name: string; ok: boolean; detail: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-app-border/40 last:border-0 text-xs">
      <div>
        <p className="font-medium text-app-text capitalize">{name}</p>
        <p className="text-app-text-dim font-mono break-all">{detail}</p>
      </div>
      <span
        className={`font-semibold shrink-0 px-2 py-0.5 rounded ${
          ok ? 'bg-app-success/15 text-app-success' : 'bg-app-error/15 text-app-error'
        }`}
      >
        {ok ? t('common.ok') : t('common.fail')}
      </span>
    </div>
  );
}

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full ${
        ok ? 'bg-app-success' : 'bg-app-error'
      }`}
    />
  );
}

function ServiceCard({ name, status }: { name: string; status: ServiceStatus }) {
  return (
    <div className="bg-app-surface border border-app-border rounded-xl p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-medium text-app-text">{name}</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-app-text-dim">{status.latency_ms}ms</span>
          <StatusDot ok={status.ok} />
        </div>
      </div>
      <div className="space-y-0">
        {Object.entries(status.checks).map(([key, check]) => (
          <CheckRow key={key} name={key} ok={check.ok} detail={check.detail} />
        ))}
      </div>
    </div>
  );
}

function useSystemStatus(autoRefresh: boolean) {
  const { token } = useAuth();
  const [data, setData] = useState<SystemStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState(30);

  const refresh = useCallback(async () => {
    const url = API_BASE
      ? `${API_BASE}${ENDPOINTS.systemStatus}`
      : new URL(ENDPOINTS.systemStatus, window.location.origin).toString();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    try {
      const res = await fetch(url, { headers });
      const nextData = await parseJsonResponse<SystemStatusResponse>(res, { url });
      if (!res.ok) {
        setError(`HTTP ${res.status}`);
        setData(null);
        return;
      }
      setData(nextData);
      setError(null);
      setLastUpdated(new Date());
      setCountdown(30);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch system status');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!autoRefresh) return;
    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          refresh();
          return 30;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [autoRefresh, refresh]);

  return { data, loading, error, lastUpdated, countdown, refresh };
}

function useMemorySummaryStats() {
  const { token } = useAuth();
  const [stats, setStats] = useState<Record<string, { count: number; latestDate: string }> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const url = API_BASE
        ? `${API_BASE}${ENDPOINTS.memorySummaryDaily}`
        : new URL(ENDPOINTS.memorySummaryDaily, window.location.origin).toString();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      try {
        const res = await fetch(url, { headers });
        const items = await parseJsonResponse<DailySummaryItem[]>(res, { url });
        if (!res.ok) {
          setStats(null);
          return;
        }
        const byOrg: Record<string, { count: number; latestDate: string }> = {};
        items.forEach((item) => {
          if (!byOrg[item.org_id]) {
            byOrg[item.org_id] = { count: 0, latestDate: item.summary_date };
          }
          byOrg[item.org_id].count += 1;
          if (item.summary_date > byOrg[item.org_id].latestDate) {
            byOrg[item.org_id].latestDate = item.summary_date;
          }
        });
        setStats(byOrg);
      } catch {
        setStats(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  return { stats, loading };
}

export default function SystemStatusPage() {
  const { t } = useTranslation();
  const [autoRefresh, setAutoRefresh] = useState(false);
  const { data, loading, error, lastUpdated, countdown, refresh } = useSystemStatus(autoRefresh);
  const { stats, loading: statsLoading } = useMemorySummaryStats();

  const relativeTime = useMemo(() => {
    if (!lastUpdated) return null;
    const seconds = Math.floor((Date.now() - lastUpdated.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  }, [lastUpdated]);

  const serviceNames = useMemo(
    () => (data ? Object.keys(data.services).sort() : []),
    [data]
  );

  return (
    <PageShell narrow>
      <PageHeader
        title={t('system.title')}
        description={t('system.description')}
      >
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-app-text-dim cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4 rounded border-app-border bg-app-surface"
            />
            {autoRefresh ? `Refreshing in ${countdown}s` : 'Auto-refresh: OFF'}
          </label>
          <button
            type="button"
            onClick={() => refresh()}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-app-accent border border-app-border hover:bg-app-muted disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </button>
        </div>
      </PageHeader>

      {relativeTime && (
        <p className="text-xs text-app-text-dim mb-4">Updated {relativeTime}</p>
      )}

      {error && (
        <p className="text-sm text-app-error bg-app-error/10 border border-app-error/30 rounded-lg p-3 mb-6">
          {t('system.errorHint', { error })}
        </p>
      )}

      {!loading && data && (
        <>
          {/* Service Cards Grid */}
          {/* Skill Service card removed — deprecated in PR1 */}

          <div className="grid gap-4 md:grid-cols-3 mb-6">
            {serviceNames.map((name) => (
              <ServiceCard key={name} name={name} status={data.services[name]} />
            ))}
          </div>

          {/* Stats + Links Row */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* Memory Summary Stats */}
            <div className="bg-app-surface border border-app-border rounded-xl p-5 shadow-sm">
              <h2 className="text-sm font-medium text-app-text mb-3">Memory Summary Stats</h2>
              {statsLoading ? (
                <p className="text-xs text-app-text-dim py-2">{t('common.loading')}</p>
              ) : stats && Object.keys(stats).length > 0 ? (
                <div className="space-y-2 text-sm">
                  {Object.entries(stats)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([org, { count, latestDate }]) => (
                      <div key={org} className="flex items-center justify-between border-b border-app-border/40 pb-2 last:border-0">
                        <span className="text-app-text font-medium">{org}</span>
                        <div className="flex gap-3 text-app-text-dim text-xs">
                          <span>{count} daily</span>
                          <span>{latestDate}</span>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-xs text-app-text-dim py-2">
                  {t('system.memorySummaryEmpty', {
                    defaultValue:
                      'No daily summaries yet. Summaries come from agent_memory (seed or future rollup), not legacy agent_chat tables.',
                  })}
                </p>
              )}
            </div>

            {/* Quick Links */}
            <div className="bg-app-surface border border-app-border rounded-xl p-5 shadow-sm">
              <h2 className="text-sm font-medium text-app-text mb-3">Quick Links</h2>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link
                    to="/chat-test"
                    className="flex items-center gap-2 text-app-accent hover:text-app-accent/80"
                  >
                    <ChatBubbleLeftRightIcon className="w-4 h-4" />
                    {t('system.agentChat')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/agent-management"
                    className="flex items-center gap-2 text-app-accent hover:text-app-accent/80"
                  >
                    <LinkIcon className="w-4 h-4" />
                    Memory Summary
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </>
      )}
    </PageShell>
  );
}
